import React, { createContext, useContext, useState } from 'react';
import Pagination from './Pagination';
import TableLoadingSpinner from './TableLoadingSpinner';
import Tooltip from './Tooltip';

interface TableProps {
  headers: string[];
  columns: string[];
  data: any[];
  page: number;
  limit: number;
  totalPages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  showPagination?: boolean;
}

type TableContextType = {
  headers: string[];
  columns: string[];
  page: number;
  limit: number;
};

const TableContext = createContext<TableContextType>({
  headers: [],
  columns: [],
  page: 0,
  limit: 3,
});

const Table: React.FC<TableProps> = ({
  headers,
  columns,
  data,
  page,
  setPage,
  isLoading,
  totalPages,
  limit,
}) => {
  return (
    <TableContext.Provider value={{ headers, columns, page, limit }}>
      <div className='flex flex-col w-full h-auto'>
        <div className='relative w-full h-full'>
          <table className='w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden h-full bg-black text-white'>
            <thead>
              <tr>
                {headers.map((column, index) => (
                  <th
                    key={index}
                    className='md:h-[32px] px-2 border-b border-[#FCCB164D] text-base text-[#FFFFFF99] font-semibold whitespace-pre-wrap text-center bg-[#FCCB164D] backdrop-blur-[20px] sticky top-0'
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='overflow-y-scroll'>
              {!isLoading &&
                data.map((row, rowIndex) => (
                  <TableRow key={rowIndex} index={rowIndex} data={row} />
                ))}
            </tbody>
          </table>
          {isLoading && (
            <div className='flex justify-center items-center absolute top-0 left-0 w-full h-full bg-transparent'>
              <TableLoadingSpinner width={'24px'} height={'24px'} />
            </div>
          )}
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </TableContext.Provider>
  );
};

export default Table;

const TableRow = ({ index, data }: { index: number; data: any }) => {
  const { columns, page, limit } = useContext(TableContext);

  const [$isHover, setIsHover] = useState(false);

  const start = page * limit;

  const onRowClicked = data => {
    window.open(
      `https://platform.spacemap42.com/?service=conjunction&board=false&cdm_id=${data.CDM_ID}`,
      '_blank'
    );
  };

  return (
    <tr
      key={`${limit}${data.pName}`}
      className={`transition-colors duration-300 cursor-pointer ${
        $isHover ? 'bg-[#e6e6e6] text-black' : 'bg-auto text-auto'
      }`}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={() => onRowClicked(data)}
    >
      <td className='md:h-[32px] px-2 border-b border-[#FCCB164D] font-medium text-sm whitespace-pre-wrap text-center'>
        <Tooltip message={data.pId}>
          <span className='hover:text-[#fccb15] hover:cursor-pointer'>
            {data.pName}
          </span>
        </Tooltip>
      </td>
      <td className='md:h-[32px] px-2 border-b border-[#FCCB164D] font-medium text-sm whitespace-pre-wrap text-center'>
        <Tooltip message={data.sId}>
          <span className='hover:text-[#fccb15] hover:cursor-pointer'>
            {data.sName}
          </span>
        </Tooltip>
      </td>
        <td className='md:h-[32px] px-2 border-b border-[#FCCB164D] font-medium text-sm whitespace-nowrap text-center'>
        {data.tcaTime}
      </td>
      <td className='md:h-[32px] px-2 border-b border-[#FCCB164D] font-medium text-sm whitespace-pre-wrap text-center'>
        {data.dca}
      </td>
      <td className='md:h-[32px] px-2 border-b border-[#FCCB164D] font-medium text-sm whitespace-pre-wrap text-center'>
        {data.poc.toExponential(2)}
      </td>
    </tr>
  );
};
